/**
 * Event Operations Dashboard (tabs) — Iter-224d / Priority 2.
 *
 * Mounted at /admin/operations/event/:shiftId.
 * Replaces the long vertical-scroll layout of /admin/shifts/:shiftId
 * with a focused 7-tab interface:
 *
 *   1. Overview   — shift meta + pipeline badge + quick actions
 *   2. Talent     — assigned roster
 *   3. Attendance — reuses AttendanceCompliancePanel
 *   4. Payroll    — per-talent hours/rate/total
 *   5. Invoices   — list + Generate / Send / Mark Paid Externally CTAs
 *   6. Reports    — post-event recaps + ratings (NOT locked until close)
 *   7. Recruiting — applicants/invites/broadcasts (auto-collapsed once shift_date past)
 *
 * Per user spec:
 *  • Horizontal scroll-strip tab list on mobile
 *  • Deep-link via ?tab=invoices etc.
 *  • Empty-state CTAs drive the next workflow step
 *  • Auto-jumps to the next-actionable tab on first load
 *  • Pipeline badge ("Ready for Payroll"…) on the Overview tab
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  ArrowLeft, Loader2, CheckCircle2, Users, ClipboardCheck, DollarSign,
  FileText, Star, UserPlus, MapPin, Calendar, Clock, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/api';
import { formatDateMMDDYYYY, formatTimeRange } from '../../utils/timeFormat';
import { resolveProfilePhoto } from '../../utils/profilePhoto';
import AttendanceCompliancePanel from '../../components/AttendanceCompliancePanel';

const TAB_DEFS = [
  { id: 'overview',    label: 'Overview',    icon: CheckCircle2 },
  { id: 'talent',      label: 'Talent',      icon: Users },
  { id: 'attendance',  label: 'Attendance',  icon: ClipboardCheck },
  { id: 'payroll',     label: 'Payouts',     icon: DollarSign },
  { id: 'invoices',    label: 'Invoices',    icon: FileText },
  { id: 'reports',     label: 'Reports',     icon: Star },
  { id: 'recruiting',  label: 'Recruiting',  icon: UserPlus },
];

const BADGE_STYLES = {
  ready_for_payroll: { label: 'Ready for Payout', cls: 'bg-blue-100 text-blue-700' },
  ready_to_invoice:  { label: 'Ready to Invoice',  cls: 'bg-cyan-100 text-cyan-700' },
  ready_to_send:     { label: 'Ready to Send',     cls: 'bg-violet-100 text-violet-700' },
  ready_to_close:    { label: 'Ready to Close',    cls: 'bg-emerald-100 text-emerald-700' },
  in_progress:       { label: 'In Progress',       cls: 'bg-gray-100 text-gray-700' },
  closed:            { label: 'Closed',            cls: 'bg-gray-200 text-gray-700' },
};

const EventOperationsDashboard = () => {
  const { shiftId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [shift, setShift] = useState(null);
  const [closeoutState, setCloseoutState] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [recaps, setRecaps] = useState([]);
  const [applicants, setApplicants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(searchParams.get('tab') || null);
  const [didAutoJump, setDidAutoJump] = useState(false);

  // Past-date check — used to auto-collapse Recruiting
  const isPastEvent = useMemo(() => {
    if (!shift?.shift_date) return false;
    return new Date(shift.shift_date) < new Date(new Date().toDateString());
  }, [shift]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Iter-224d — One source of truth: the closeout state endpoint
      // already returns shift_date / venue / event_title / role + per-step
      // completion. Fetch supplementary lists in parallel; tolerate 404s
      // (e.g., shift has no applicants yet, recap endpoint absent on
      // older deployments — UI just shows empty states).
      const [co, bk, inv, rc, ap] = await Promise.all([
        api.get(`/operations/closeout/${shiftId}`),
        api.get(`/admin/shifts/${shiftId}/applicants`).catch(() => ({ data: [] })),
        api.get(`/invoicing/admin/all?shift_id=${shiftId}&limit=20`).catch(() => ({ data: { invoices: [] } })),
        api.get(`/post-event-recap/by-shift/${shiftId}`).catch(() => ({ data: [] })),
        api.get(`/admin/shifts/${shiftId}/recipients`).catch(() => ({ data: [] })),
      ]);
      setCloseoutState(co.data);
      // Surface shift meta from the closeout payload.
      setShift({
        shift_date: co.data?.shift_date,
        venue_name: co.data?.venue_name,
        event_title: co.data?.event_title || co.data?.series_name,
        role_name: co.data?.role_name,
        // start/end time not on closeout payload — request from shifts when available
        start_time: null,
        end_time: null,
      });
      setBookings(Array.isArray(bk.data) ? bk.data : bk.data?.bookings || []);
      const invList = inv.data?.invoices || inv.data?.data || (Array.isArray(inv.data) ? inv.data : []);
      setInvoices(invList);
      setRecaps(Array.isArray(rc.data) ? rc.data : rc.data?.recaps || []);
      setApplicants(Array.isArray(ap.data) ? ap.data : ap.data?.applicants || ap.data?.recipients || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load operations data');
    } finally { setLoading(false); }
  }, [shiftId]);

  useEffect(() => { load(); }, [load]);

  // Auto-jump to the next actionable tab on first load — per user spec #5.
  useEffect(() => {
    if (didAutoJump || !closeoutState || tab) return;
    let target = 'overview';
    const steps = closeoutState.steps;
    if (steps?.attendance_approved?.done === false) target = 'attendance';
    else if (steps?.payroll_approved?.done === false) target = 'payroll';
    else if (steps?.invoice_generated?.done === false) target = 'invoices';
    else if (steps?.invoice_sent?.done === false) target = 'invoices';
    else if (steps?.event_closed?.done === false) target = 'overview'; // CloseOut wizard is separate
    else target = 'reports';
    setTab(target);
    setDidAutoJump(true);
  }, [closeoutState, didAutoJump, tab]);

  const switchTab = (newTab) => {
    setTab(newTab);
    setSearchParams({ tab: newTab });
  };

  const currentBadge = useMemo(() => {
    if (!closeoutState) return BADGE_STYLES.in_progress;
    const s = closeoutState.steps;
    if (s.event_closed.done) return BADGE_STYLES.closed;
    if (s.invoice_sent.done) return BADGE_STYLES.ready_to_close;
    if (s.invoice_generated.done) return BADGE_STYLES.ready_to_send;
    if (s.payroll_approved.done) return BADGE_STYLES.ready_to_invoice;
    if (s.attendance_approved.done) return BADGE_STYLES.ready_for_payroll;
    return BADGE_STYLES.in_progress;
  }, [closeoutState]);

  if (loading || !shift) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="ops-loading">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  // Visible tabs: hide Recruiting on past events by default (per user spec)
  const visibleTabs = TAB_DEFS.filter((t) => t.id !== 'recruiting' || !isPastEvent);
  const recruitingHidden = isPastEvent;

  return (
    <div className="max-w-6xl mx-auto p-3 sm:p-5 space-y-4" data-testid="event-operations-dashboard">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="ops-back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-base sm:text-lg font-semibold text-gray-900 truncate" data-testid="ops-event-title">
            {shift.event_title || shift.venue_name || 'Untitled event'}
          </h1>
          <p className="text-xs text-gray-500 flex flex-wrap gap-x-3 gap-y-1 mt-0.5">
            {shift.shift_date && (
              <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {formatDateMMDDYYYY(shift.shift_date)}</span>
            )}
            {shift.start_time && shift.end_time && (
              <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {formatTimeRange(shift.start_time, shift.end_time)}</span>
            )}
            {shift.venue_name && (
              <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {shift.venue_name}</span>
            )}
          </p>
        </div>
        <Badge className={currentBadge.cls} data-testid="ops-pipeline-badge">{currentBadge.label}</Badge>
        <Button
          size="sm"
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
          onClick={() => navigate(`/admin/operations/closeout/${shiftId}`)}
          data-testid="ops-closeout-btn"
        >
          <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Close Out
        </Button>
      </div>

      <Tabs value={tab || 'overview'} onValueChange={switchTab}>
        {/* Mobile scroll-strip per user spec #1 */}
        <TabsList className="w-full justify-start gap-1 overflow-x-auto flex-nowrap p-1" data-testid="ops-tab-list">
          {visibleTabs.map((t) => {
            const Icon = t.icon;
            return (
              <TabsTrigger
                key={t.id}
                value={t.id}
                className="flex items-center gap-1.5 shrink-0"
                data-testid={`ops-tab-${t.id}`}
              >
                <Icon className="w-3.5 h-3.5" /> {t.label}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {/* === OVERVIEW === */}
        <TabsContent value="overview" data-testid="ops-overview-content" className="space-y-3 mt-3">
          <Card>
            <CardHeader className="py-3 px-5">
              <CardTitle className="text-sm font-semibold">Pipeline</CardTitle>
            </CardHeader>
            <CardContent className="px-5 pb-4">
              <div className="flex flex-wrap gap-2 text-xs">
                {closeoutState && Object.entries(closeoutState.steps).map(([k, v]) => (
                  <Badge
                    key={k}
                    variant={v.done ? 'default' : 'outline'}
                    className={v.done ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100' : ''}
                    data-testid={`ops-step-badge-${k}`}
                  >
                    {v.done ? '✓' : '○'} {k.replace(/_/g, ' ')}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="py-3 px-5">
              <CardTitle className="text-sm font-semibold">Quick Stats</CardTitle>
            </CardHeader>
            <CardContent className="px-5 pb-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
              <Stat label="Bookings" value={bookings.length} />
              <Stat label="Clocked Out" value={bookings.filter((b) => b.clock_out_time).length} />
              <Stat label="Invoices" value={invoices.length} />
              <Stat label="Recap" value={recaps.length} />
            </CardContent>
          </Card>
        </TabsContent>

        {/* === TALENT === */}
        <TabsContent value="talent" data-testid="ops-talent-content" className="space-y-2 mt-3">
          {bookings.length === 0 ? (
            <EmptyCTA icon={Users} text="No talent assigned yet." action="Open shift to invite →"
              onClick={() => navigate(`/admin/shifts/${shiftId}`)} testid="ops-talent-empty-cta" />
          ) : (
            <Card>
              <CardContent className="p-0">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50"><tr>
                    <th className="text-left p-2">Talent</th>
                    <th className="text-left p-2">Status</th>
                    <th className="text-left p-2">Role</th>
                    <th className="text-right p-2">Hours</th>
                  </tr></thead>
                  <tbody data-testid="ops-talent-table">
                    {bookings.map((b, i) => {
                      const photo = resolveProfilePhoto(b);
                      return (
                        <tr key={b.id || b.booking_id || i} className="border-t" data-testid={`ops-talent-row-${i}`}>
                          <td className="p-2 flex items-center gap-2">
                            {photo ? (
                              <img src={photo} alt="" className="w-7 h-7 rounded-full object-cover" />
                            ) : <div className="w-7 h-7 rounded-full bg-gray-200" />}
                            <span>{b.first_name || ''} {b.last_name || b.talent_name || ''}</span>
                          </td>
                          <td className="p-2 text-gray-600">{b.status}</td>
                          <td className="p-2 text-gray-600">{b.role_name || '—'}</td>
                          <td className="p-2 text-right">{b.hours_approved ?? b.hours_worked ?? '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* === ATTENDANCE === */}
        <TabsContent value="attendance" data-testid="ops-attendance-content" className="mt-3">
          <AttendanceCompliancePanel shiftId={shiftId} />
        </TabsContent>

        {/* === PAYROLL === */}
        <TabsContent value="payroll" data-testid="ops-payroll-content" className="space-y-2 mt-3">
          {bookings.filter((b) => b.status === 'confirmed' || b.status === 'completed').length === 0 ? (
            <EmptyCTA icon={DollarSign} text="No payouts yet — talent must be confirmed first."
              action="View Talent →" onClick={() => switchTab('talent')}
              testid="ops-payroll-empty-cta" />
          ) : (
            <Card>
              <CardContent className="p-0">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50"><tr>
                    <th className="text-left p-2">Talent</th>
                    <th className="text-right p-2">Hours Approved</th>
                    <th className="text-right p-2">Rate</th>
                    <th className="text-right p-2">Total</th>
                  </tr></thead>
                  <tbody data-testid="ops-payroll-table">
                    {bookings.filter((b) => ['confirmed', 'completed'].includes(b.status)).map((b, i) => {
                      const hrs = Number(b.hours_approved ?? b.hours_worked ?? 0);
                      const rate = Number(b.pay_rate ?? b.talent_pay_rate ?? b.effective_pay_rate ?? 0);
                      return (
                        <tr key={b.id || i} className="border-t" data-testid={`ops-payroll-row-${i}`}>
                          <td className="p-2">{b.first_name || ''} {b.last_name || ''}</td>
                          <td className="p-2 text-right">{hrs.toFixed(2)}</td>
                          <td className="p-2 text-right">${rate.toFixed(2)}</td>
                          <td className="p-2 text-right font-medium">${(hrs * rate).toFixed(2)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* === INVOICES === */}
        <TabsContent value="invoices" data-testid="ops-invoices-content" className="space-y-2 mt-3">
          {invoices.length === 0 ? (
            <EmptyCTA icon={FileText} text="No invoices yet."
              action="Open Closeout Wizard to generate →"
              onClick={() => navigate(`/admin/operations/closeout/${shiftId}`)}
              testid="ops-invoices-empty-cta" />
          ) : (
            <Card>
              <CardContent className="p-0">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50"><tr>
                    <th className="text-left p-2">Invoice #</th>
                    <th className="text-left p-2">Status</th>
                    <th className="text-right p-2">Total</th>
                    <th className="text-right p-2">Due</th>
                    <th className="p-2"></th>
                  </tr></thead>
                  <tbody data-testid="ops-invoices-table">
                    {invoices.map((inv, i) => (
                      <tr key={inv.id || i} className="border-t" data-testid={`ops-invoice-row-${i}`}>
                        <td className="p-2 font-mono">{inv.invoice_number}</td>
                        <td className="p-2 text-gray-600">{inv.payment_status || inv.status}</td>
                        <td className="p-2 text-right">${Number(inv.total_amount || 0).toFixed(2)}</td>
                        <td className="p-2 text-right">${Number(inv.amount_due || 0).toFixed(2)}</td>
                        <td className="p-2 text-right">
                          <Button size="sm" variant="outline"
                            onClick={() => navigate(`/admin/invoices?invoice=${inv.id}`)}>Open</Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* === REPORTS === */}
        <TabsContent value="reports" data-testid="ops-reports-content" className="space-y-2 mt-3">
          {recaps.length === 0 ? (
            <EmptyCTA icon={Star} text="No post-event recaps submitted yet."
              action={null} testid="ops-reports-empty-cta" />
          ) : (
            <div className="space-y-2">
              {recaps.map((r, i) => (
                <Card key={r.id || i} data-testid={`ops-recap-row-${i}`}>
                  <CardContent className="p-3 text-xs">
                    <div className="font-medium">{r.talent_name || r.first_name || 'Talent'}</div>
                    <div className="text-gray-500 mt-0.5">{r.summary || r.feedback || '—'}</div>
                    {r.rating && (
                      <Badge className="mt-1 bg-amber-100 text-amber-700">
                        <Star className="w-3 h-3 mr-1" /> {Number(r.rating).toFixed(1)}
                      </Badge>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* === RECRUITING === (hidden on past events per user spec) */}
        {!recruitingHidden && (
          <TabsContent value="recruiting" data-testid="ops-recruiting-content" className="space-y-2 mt-3">
            {applicants.length === 0 ? (
              <EmptyCTA icon={UserPlus} text="No applicants yet."
                action="Open shift to invite talent →"
                onClick={() => navigate(`/admin/shifts/${shiftId}`)}
                testid="ops-recruiting-empty-cta" />
            ) : (
              <Card>
                <CardContent className="p-0">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50"><tr>
                      <th className="text-left p-2">Applicant</th>
                      <th className="text-left p-2">Status</th>
                      <th className="text-right p-2">Match</th>
                    </tr></thead>
                    <tbody data-testid="ops-recruiting-table">
                      {/* Iter-P0-20260801 PO7 — removed .slice(0,50) cap
                          that was silently hiding applicants beyond the
                          first 50. Rely on the table's natural overflow
                          instead. */}
                      {applicants.map((a, i) => (
                        <tr key={a.id || i} className="border-t" data-testid={`ops-recruit-row-${i}`}>
                          <td className="p-2">{a.first_name || ''} {a.last_name || ''}</td>
                          <td className="p-2 text-gray-600">{a.status || 'pending'}</td>
                          <td className="p-2 text-right">{a.match_score ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        )}
      </Tabs>

      {recruitingHidden && (
        <p className="text-[11px] text-gray-400 flex items-center gap-1 mt-2" data-testid="ops-recruiting-collapsed-note">
          <AlertTriangle className="w-3 h-3" /> Recruiting tab hidden — event date has passed.{' '}
          <button className="underline" onClick={() => navigate(`/admin/shifts/${shiftId}`)}>
            View on shift detail page
          </button>
        </p>
      )}
    </div>
  );
};

const Stat = ({ label, value }) => (
  <div className="text-center p-2 bg-gray-50 rounded">
    <p className="text-lg font-semibold text-gray-900">{value}</p>
    <p className="text-[10px] text-gray-500 uppercase tracking-wide">{label}</p>
  </div>
);

const EmptyCTA = ({ icon: Icon, text, action, onClick, testid }) => (
  <Card className="border-dashed">
    <CardContent className="p-8 text-center" data-testid={testid}>
      <Icon className="w-8 h-8 mx-auto text-gray-300 mb-2" />
      <p className="text-sm text-gray-600">{text}</p>
      {action && (
        <Button size="sm" variant="outline" className="mt-3" onClick={onClick}>
          {action}
        </Button>
      )}
    </CardContent>
  </Card>
);

export default EventOperationsDashboard;
